import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase, type PortfolioProject } from '@/lib/supabase';

const CATEGORY_COLORS: Record<string, string> = {
  'AI / ML': 'bg-violet-50 text-violet-700 border-violet-100',
  'Web App': 'bg-sky-50 text-sky-700 border-sky-100',
  'CLI Tool': 'bg-amber-50 text-amber-700 border-amber-100',
  'Backend': 'bg-rose-50 text-rose-700 border-rose-100',
  'Full Stack': 'bg-emerald-50 text-emerald-700 border-emerald-100',
  'Other': 'bg-gray-50 text-gray-700 border-gray-100',
};

export default function FeaturedProjects() {
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      const { data } = await supabase
        .from('portfolio_projects')
        .select('*')
        .eq('featured', true)
        .order('sort_order', { ascending: true })
        .limit(3);
      if (data) setProjects(data as PortfolioProject[]);
      setLoading(false);
    };
    fetchProjects();
  }, []);

  return (
    <section id="projects" className="py-24 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <h2
            className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Featured
            <br />
            Projects
          </h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <p className="text-gray-500 max-w-xs text-sm leading-relaxed">
              A selection of my most impactful work — from AI tools to production-grade APIs.
            </p>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              All Projects
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl bg-gray-100 animate-pulse h-80"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {projects.map((project, idx) => (
              <article
                key={project.id}
                className={`rounded-2xl overflow-hidden flex flex-col group transition-transform duration-300 hover:-translate-y-1 cursor-pointer ${
                  idx === 0 ? 'bg-gray-900 text-white' : 'bg-white border border-gray-100'
                }`}
              >
                {/* Image */}
                <div className="relative overflow-hidden h-48">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 ${idx === 0 ? 'bg-gray-900/30' : 'bg-white/10'}`}></div>
                  <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full border ${CATEGORY_COLORS[project.category] || CATEGORY_COLORS['Other']}`}>
                    {project.category}
                  </span>
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1 gap-3">
                  <h3 className={`font-bold text-lg leading-snug ${idx === 0 ? 'text-white' : 'text-gray-900'}`}>
                    {project.title}
                  </h3>
                  <p className={`text-sm leading-relaxed line-clamp-2 flex-1 ${idx === 0 ? 'text-gray-300' : 'text-gray-500'}`}>
                    {project.description}
                  </p>

                  {/* Tech Tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {project.technologies.slice(0, 3).map((tech) => (
                      <span
                        key={tech}
                        className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                          idx === 0
                            ? 'bg-white/10 text-gray-300'
                            : 'bg-gray-50 text-gray-600 border border-gray-100'
                        }`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Links */}
                  <div className="flex items-center gap-3 pt-1">
                    {project.demo_url && (
                      <a
                        href={project.demo_url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                          idx === 0
                            ? 'bg-white text-gray-900 hover:bg-gray-100'
                            : 'bg-gray-900 text-white hover:bg-gray-700'
                        }`}
                      >
                        <i className="ri-external-link-line"></i>
                        Live Demo
                      </a>
                    )}
                    {project.github_url && (
                      <a
                        href={project.github_url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className={`inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer whitespace-nowrap ${
                          idx === 0 ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                        } transition-colors`}
                      >
                        <i className="ri-github-fill text-base"></i>
                        Source
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}